import {
  Injectable,
  computed,
  inject,
  linkedSignal,
  resource,
  signal,
} from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { PushDestinationService } from '../../shared/services/push-destination/push-destination.service';
import { PushDestination } from '../../shared/interfaces/push-destination.interface';
import {
  PushDestinationDisplay,
  buildPushDestinationDisplays,
} from './push-destination-display.mapper';

/** Transient result banner for the last "Send Test" action. */
export interface PushDestinationTestFeedback {
  destinationId: number;
  success: boolean;
  message: string;
}

/**
 * Owns all state and data loading for the push-destinations list. The component
 * injects this and only renders + dispatches UI dialogs — there is no load
 * orchestration in the component.
 *
 * Follows the connections reference pattern (SCRUM-761): the project id is a
 * signal, a `resource()` loads the destinations list, and `linkedSignal` keeps
 * the previously built rows visible while a refetch is in flight. `firstValueFrom`
 * bridges the Observable HttpClient at the data boundary; the component sees only
 * signals.
 *
 * Provided at the component (`providers: [PushDestinationsListStore]`) so its
 * lifetime matches the list.
 */
@Injectable()
export class PushDestinationsListStore {
  readonly #service = inject(PushDestinationService);

  // ── Inputs (driven by the component) ────────────────────────────────────────
  readonly #inputProjectId = signal<number | null>(null);

  /** Set the project id from the component input. */
  setProjectId(projectId: number | null): void {
    this.#inputProjectId.set(projectId);
  }

  /**
   * Resolved project id for API calls: the input id when valid, otherwise the
   * app-state id. Returns `null` when no valid id is available, which idle-gates
   * the resource.
   */
  readonly #resolvedProjectId = computed<number | null>(() => {
    const input = this.#inputProjectId();
    if (input != null && input > 0) {
      return input;
    }
    const appState = this.#service.getCurrentProjectId();
    return appState != null && appState > 0 ? appState : null;
  });

  /** The valid project id currently in use, or `null`. */
  readonly projectId = this.#resolvedProjectId;

  // ── Resource ─────────────────────────────────────────────────────────────────
  readonly #listResource = resource<PushDestination[], number | undefined>({
    params: () => this.#resolvedProjectId() ?? undefined,
    loader: ({ params }) => firstValueFrom(this.#service.list(params!)),
  });

  readonly #rows = computed<PushDestinationDisplay[] | undefined>(() => {
    if (!this.#listResource.hasValue()) {
      // Still loading the first time — let `displayDestinations` retain previous.
      return undefined;
    }
    return buildPushDestinationDisplays(this.#listResource.value());
  });

  /** Display rows; retains the previously built rows while a refetch is in flight. */
  readonly displayDestinations = linkedSignal<
    PushDestinationDisplay[] | undefined,
    PushDestinationDisplay[]
  >({
    source: () => this.#rows(),
    computation: (value, previous) => value ?? previous?.value ?? [],
  });

  readonly count = computed(() => this.displayDestinations().length);

  readonly loading = computed(() => this.#listResource.isLoading());

  // ── Test feedback ─────────────────────────────────────────────────────────────
  readonly #testingDestinationId = signal<number | null>(null);
  /** Id of the destination currently being test-sent, or `null`. */
  readonly testingDestinationId = this.#testingDestinationId.asReadonly();

  readonly #testResult = signal<PushDestinationTestFeedback | null>(null);
  /** Result banner for the last test, or `null` once dismissed. */
  readonly testResult = this.#testResult.asReadonly();

  /** Dismiss the test-result banner. */
  clearTestResult(): void {
    this.#testResult.set(null);
  }

  // ── Commands ────────────────────────────────────────────────────────────────
  /** Refetch the destinations list with the current params. */
  reload(): void {
    this.#listResource.reload();
  }

  /** Delete a destination, then refetch. Rejects on API error. */
  async deleteDestination(destination: PushDestinationDisplay): Promise<void> {
    const projectId = this.#resolvedProjectId();
    if (!projectId) {
      throw new Error('Missing project id for delete');
    }
    await firstValueFrom(
      this.#service.delete(projectId, destination.destinationId),
    );
    this.reload();
  }

  /** Flip a destination's enabled flag, then refetch. Rejects on API error. */
  async toggleEnabled(destination: PushDestinationDisplay): Promise<void> {
    const projectId = this.#resolvedProjectId();
    if (!projectId) {
      throw new Error('Missing project id for toggle');
    }
    await firstValueFrom(
      this.#service.update(projectId, destination.destinationId, {
        enabled: !destination.enabled,
      }),
    );
    this.reload();
  }

  /**
   * Send a test notification to a destination and record the outcome in the
   * `testResult` banner. Never rejects — failures land in the banner.
   */
  async testDestination(destination: PushDestinationDisplay): Promise<void> {
    const projectId = this.#resolvedProjectId();
    if (!projectId) {
      return;
    }
    this.#testingDestinationId.set(destination.destinationId);
    this.#testResult.set(null);
    try {
      const result = await firstValueFrom(
        this.#service.test(projectId, destination.destinationId),
      );
      // Server returns { status: "ok", message } on success (no `success` boolean).
      const isSuccess =
        result?.success === true ||
        (result as { status?: string })?.status === 'ok';
      this.#testResult.set({
        destinationId: destination.destinationId,
        success: isSuccess,
        message:
          result?.message ||
          (isSuccess ? 'Test notification sent successfully' : 'Test failed'),
      });
    } catch (error) {
      this.#testResult.set({
        destinationId: destination.destinationId,
        success: false,
        message:
          (error as { error?: { message?: string } })?.error?.message ||
          'Failed to send test notification',
      });
    } finally {
      this.#testingDestinationId.set(null);
    }
  }
}
